import {
    Component,
    ChangeDetectionStrategy,
    OnInit,
    ViewChild,
    ChangeDetectorRef,
    OnDestroy
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { MotorsPackageTypeEnum, SellingTypeEnum } from '@classifieds-sell/datalayer';
import { FormHelperService, isEditPage, isRelistPage, isEditOrRelistPage, ValidatorPipe } from '@classifieds-sell/shared';
import { MotorsDraftFacade } from '@classifieds-sell/state';
import { Subject } from 'rxjs';
import { MotorsSectionBase } from '../motors-section-base';

@Component({
    selector: 'motors-description-and-photos-description-section',
    templateUrl: './motors-description-and-photos-description-section.component.html',
    styleUrls: ['./motors-description-and-photos-description-section.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class MotorsDescriptionAndPhotosDescriptionSectionComponent
    extends MotorsSectionBase
    implements OnInit, OnDestroy {

    private _destroyed$: Subject<void> = new Subject<void>();
    private _isEditOrRelistPage = false;
    private _isRelistPage = false;

    @ViewChild('motorsDescriptionAndPhotosDescriptionSectionForm')
    motorsDescriptionAndPhotosDescriptionSectionForm: NgForm;

    maxTitleWordLength = 20;
    isDisabledTitle = false;
    isDisabledDescription = false;
    isDisabledSubTitle = false;
    titleDescription: string;

    constructor(
        draftFacade: MotorsDraftFacade,
        private _changeDetectorRef: ChangeDetectorRef,
        private _formHelperService: FormHelperService,
        private _validatorPipe: ValidatorPipe,
        private _activatedRoute: ActivatedRoute
    ) {
        super(draftFacade);
    }

    ngOnDestroy(): void {
        super.onDestroy();
        this._destroyed$.next();
    }

    ngOnInit(): void {
        super.onInit(() => {
            this._isEditOrRelistPage = isEditOrRelistPage(this._activatedRoute);
            this._isRelistPage = isRelistPage(this._activatedRoute);
            if (this._isRelistPage) {
                this.titleDescription = 'Title cannot be edited when relisting';
            }
            this.handleDisableInputs();
            this.clearSubTitleWhenListingTypeChange();
            this._changeDetectorRef.markForCheck();
        });
    }

    isValid(): boolean {
        this._formHelperService.validate(
            this.motorsDescriptionAndPhotosDescriptionSectionForm
        );

        return (this._isEditOrRelistPage && this.motorsDescriptionAndPhotosDescriptionSectionForm.disabled)
            || this.motorsDescriptionAndPhotosDescriptionSectionForm.form.valid;
    }

    dirty(): boolean {
        return this.motorsDescriptionAndPhotosDescriptionSectionForm.form.dirty;
    }

    private get isPremiumPackageSelected(): boolean {
        return this.draftData.listingDetails.categoryPackageId === MotorsPackageTypeEnum.PREMIUM;
    }

    private handleDisableInputs() {
        if (isEditPage(this._activatedRoute)) {
            const isAuction = this.originalDraftData.listingDetails.sellingType === SellingTypeEnum.auction;
            this.isDisabledTitle = this.isListingAfterSevenDays || this.isWithBid || this.isReserveMet;
            this.isDisabledDescription = isAuction && (this.isWithBid || this.isReserveMet);
            this.isDisabledSubTitle = this.isDisabledDescription || !this.isPremiumPackageSelected;
        } else if (this._isRelistPage) {
            this.isDisabledTitle = true;
            this.isDisabledSubTitle = !this.isPremiumPackageSelected;
        } else {
            this.isDisabledSubTitle = !this.isPremiumPackageSelected;
        }
    }

    private clearSubTitleWhenListingTypeChange() {
        if (this.draftData.listingDetails.categoryPackageId !== MotorsPackageTypeEnum.PREMIUM
            && this.draftData.descriptionAndPhotos.subTitle) {
            this.draftData.descriptionAndPhotos.subTitle = null;
            this.publish();
        }
    }

    private hasNoWordLongerThan(str: string, maxLength: number) {
        if (!str){
            return true;
        }

        const longestWord = str
            .split(' ')
            .map(a => a.length)
            .reduce((a, b) => a > b ? a : b, 0);

        return longestWord <= maxLength;
    }

    listingTitleValidation() {
        return this._validatorPipe.transform({
            name: 'ListingTitle',
            validations: {
                required: { value: true },
                maxLength: { value: 50 },
                customValidation: {
                    errorMessage: `Words in title must not be longer than ${this.maxTitleWordLength} characters`,
                    enable: true,
                    validate: () => {
                        return this.hasNoWordLongerThan(
                            this.draftData.descriptionAndPhotos.listingTitle as string, this.maxTitleWordLength
                        );
                    }
                }
            }
        });
    }

    subtitleValidation() {
        return this._validatorPipe.transform({
            name: 'SubTitle',
            validations: {
                required: { value: false },
                maxLength: { value: 50 },
                customValidation: {
                    errorMessage: `Words in subtitle must not be longer than ${this.maxTitleWordLength} characters`,
                    enable: true,
                    validate: () => {
                        return this.hasNoWordLongerThan(
                            this.draftData.descriptionAndPhotos.subTitle as string, this.maxTitleWordLength
                        );
                    }
                }
            }
        });
    }
}
